/**
 * The WAI-ARIA "tabs" keyboard contract in ONE place: roving tabindex + ArrowLeft/ArrowRight/
 * Home/End, moving focus to the newly selected tab.
 *
 * It lives here rather than only inside a tab COMPONENT because several product surfaces need the
 * behaviour without that component's chrome — a segmented control, a chip row carrying counts — and
 * the alternative was what the audit found: `role="tab"` markup with click handlers and nothing
 * else. The role tells a screen-reader user "tab, 1 of 3"; they then press Left/Right (and expect a
 * single Tab stop for the set) and nothing happens. axe cannot see it — `aria-required-children`
 * and `aria-selected` are all satisfied — so the gate stays green while the announced pattern is a
 * lie. Anything that renders `role="tab"` must use this (or a component built on it).
 *
 * Usage on a hand-styled tab button:
 *   role="tab" id={`${idPrefix}-${tab.id}`} aria-controls={`${panelIdPrefix}-${tab.id}`}
 *   aria-selected={selected} tabIndex={selected ? 0 : -1}
 *   onKeyDown={(e) => moveRovingTab(e, { tabs, index, onChange, idPrefix })}
 * …and give the panel `role="tabpanel" id={`${panelIdPrefix}-${value}`}
 * aria-labelledby={`${idPrefix}-${value}`}`, or the association the role promises does not exist.
 */
export function moveRovingTab(event, { tabs, index, onChange, idPrefix }) {
  const count = Array.isArray(tabs) ? tabs.length : 0;
  if (!count) return;

  let nextIndex = -1;
  if (event.key === "ArrowRight") nextIndex = (index + 1) % count;
  else if (event.key === "ArrowLeft") nextIndex = (index - 1 + count) % count;
  else if (event.key === "Home") nextIndex = 0;
  else if (event.key === "End") nextIndex = count - 1;
  if (nextIndex < 0) return;

  event.preventDefault();
  const nextTab = tabs[nextIndex];
  onChange?.(nextTab.id);
  document.getElementById(`${idPrefix}-${nextTab.id}`)?.focus();
}

export default moveRovingTab;
